import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiArrowsClockwiseBold,
    PiLifebuoyBold,
    PiPaperPlaneTiltBold,
} from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Badge, Button, Input, LoadingState, Text } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { PloomesDealDetail } from '../types';
import DealOwnerCell from '../DealOwnerCell';
import DetailCard from './DetailCard';

type ZendeskComment = {
    id: number;
    zendesk_comment_id: number;
    author_name: string | null;
    author_email: string | null;
    author_avatar_url?: string | null;
    body: string;
    public: boolean;
    zendesk_created_at: string | null;
};

type ZendeskTicketRow = {
    id: number;
    zendesk_id: number;
    subject: string;
    ticket_url: string;
    status: string | null;
    priority: string | null;
    requester_email: string | null;
    created_by: { id: number; name: string; email: string; avatar_url?: string | null } | null;
    last_synced_at: string | null;
    created_at: string | null;
    comments_count: number;
    comments?: ZendeskComment[];
};

type SubjectSuggestion = {
    key: string;
    label: string;
    subject: string;
    motivo?: string;
};

type ZendeskDraft = {
    sale_number: string;
    default_subject: string;
    default_motivo?: string;
    subject_suggestions: SubjectSuggestion[];
    routing?: { form: string; group: string; area: string };
    summary: string;
};

type Props = {
    dealId: number;
    deal?: Pick<
        PloomesDealDetail,
        'display_sale_number' | 'internal_number' | 'deal_number' | 'title' | 'contact_name'
    > | null;
};

function statusColor(status: string | null | undefined): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    switch ((status || '').toLowerCase()) {
        case 'solved':
        case 'closed':
            return 'success';
        case 'pending':
        case 'hold':
            return 'warning';
        case 'open':
        case 'new':
            return 'info';
        default:
            return 'secondary';
    }
}

function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return '—';
    }
    try {
        return new Intl.DateTimeFormat('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch {
        return value;
    }
}

function fallbackSaleNumber(deal: Props['deal'], dealId: number): string {
    return (
        deal?.display_sale_number?.toString().trim() ||
        deal?.internal_number?.toString().trim() ||
        deal?.deal_number?.toString().trim() ||
        String(dealId)
    );
}

export default function PloomesDealZendeskPanel({ dealId, deal = null }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [subjectKey, setSubjectKey] = useState('comss_duvidas');
    const [subject, setSubject] = useState('');
    const [motivo, setMotivo] = useState('comss_duvidas');
    const [comment, setComment] = useState('');
    const [replyBody, setReplyBody] = useState('');
    const [summaryOpen, setSummaryOpen] = useState(false);

    const saleFallback = fallbackSaleNumber(deal, dealId);

    const listQuery = useQuery({
        queryKey: ['ploomes-deal-zendesk-tickets', dealId],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{
                data: ZendeskTicketRow[];
                meta?: { zendesk_agent_url?: string | null };
            }>(`/ploomes-deals/${dealId}/zendesk-tickets`, { signal });
            return {
                tickets: data.data,
                agentUrl: data.meta?.zendesk_agent_url ?? null,
            };
        },
        enabled: Boolean(dealId),
        staleTime: 30_000,
    });

    const draftQuery = useQuery({
        queryKey: ['ploomes-deal-zendesk-draft', dealId],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{ data: ZendeskDraft }>(
                `/ploomes-deals/${dealId}/zendesk-tickets/draft`,
                { signal, timeout: 60_000 },
            );
            return data.data;
        },
        enabled: Boolean(dealId) && showCreateModal,
        staleTime: 60_000,
    });

    const suggestions = useMemo<SubjectSuggestion[]>(() => {
        if (draftQuery.data?.subject_suggestions?.length) {
            return draftQuery.data.subject_suggestions;
        }
        const sale = draftQuery.data?.sale_number || saleFallback;
        return [
            { key: 'comss_duvidas', label: t('v2.ploomes_deals.zendesk_motivo_duvidas'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_duvidas')}`, motivo: 'comss_duvidas' },
            { key: 'comss_valor', label: t('v2.ploomes_deals.zendesk_motivo_valor'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_valor')}`, motivo: 'comss_valor' },
            { key: 'comss_porcentagem', label: t('v2.ploomes_deals.zendesk_motivo_porcentagem'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_porcentagem')}`, motivo: 'comss_porcentagem' },
            { key: 'comss_excecao', label: t('v2.ploomes_deals.zendesk_motivo_excecao'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_excecao')}`, motivo: 'comss_excecao' },
            { key: 'comss_team_agreement_sdr', label: t('v2.ploomes_deals.zendesk_motivo_team'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_team')}`, motivo: 'comss_team_agreement_sdr' },
            { key: 'comss_venda_não_apareceu', label: t('v2.ploomes_deals.zendesk_motivo_venda'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_venda')}`, motivo: 'comss_venda_não_apareceu' },
            { key: 'comss_outros', label: t('v2.ploomes_deals.zendesk_motivo_outros'), subject: `Pedido #${sale} — ${t('v2.ploomes_deals.zendesk_motivo_outros')}`, motivo: 'comss_outros' },
        ];
    }, [draftQuery.data, saleFallback, t]);

    useEffect(() => {
        if (!showCreateModal) {
            return;
        }
        const selected = suggestions.find((item) => item.key === subjectKey) ?? suggestions[0];
        if (!selected) {
            return;
        }
        setMotivo(selected.motivo || selected.key);
        if (subjectKey !== 'comss_outros' || !subject.trim() || subject.endsWith('— ')) {
            setSubject(selected.subject);
        }
    }, [showCreateModal, suggestions, subjectKey]);

    const detailQuery = useQuery({
        queryKey: ['ploomes-deal-zendesk-ticket', dealId, selectedId],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{ data: ZendeskTicketRow }>(
                `/ploomes-deals/${dealId}/zendesk-tickets/${selectedId}?sync=1`,
                { signal, timeout: 60_000 },
            );
            return data.data;
        },
        enabled: Boolean(dealId && selectedId),
        staleTime: 15_000,
    });

    const createMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{
                message: string;
                id: number;
                ticket_id: number;
                ticket_url: string;
            }>(`/ploomes-deals/${dealId}/zendesk-tickets`, {
                subject: subject.trim() || undefined,
                comment: comment.trim(),
                motivo,
            });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message || t('v2.ploomes_deals.zendesk_ticket_success', { id: data.ticket_id }));
            setShowCreateModal(false);
            setSubjectKey('comss_duvidas');
            setMotivo('comss_duvidas');
            setSubject('');
            setComment('');
            setSummaryOpen(false);
            queryClient.invalidateQueries({ queryKey: ['ploomes-deal-zendesk-tickets', dealId] });
            setSelectedId(data.id);
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                t('v2.ploomes_deals.zendesk_ticket_error');
            toast.error(msg);
        },
    });

    const syncMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ message: string; data: ZendeskTicketRow }>(
                `/ploomes-deals/${dealId}/zendesk-tickets/${selectedId}/sync`,
            );
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message || t('v2.ploomes_deals.zendesk_sync_success'));
            queryClient.setQueryData(['ploomes-deal-zendesk-ticket', dealId, selectedId], data.data);
            queryClient.invalidateQueries({ queryKey: ['ploomes-deal-zendesk-tickets', dealId] });
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                t('v2.ploomes_deals.zendesk_sync_error');
            toast.error(msg);
        },
    });

    const replyMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ message: string; data: ZendeskTicketRow }>(
                `/ploomes-deals/${dealId}/zendesk-tickets/${selectedId}/comments`,
                { body: replyBody.trim(), public: true },
            );
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message || t('v2.ploomes_deals.zendesk_reply_success'));
            setReplyBody('');
            queryClient.setQueryData(['ploomes-deal-zendesk-ticket', dealId, selectedId], data.data);
            queryClient.invalidateQueries({ queryKey: ['ploomes-deal-zendesk-tickets', dealId] });
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                t('v2.ploomes_deals.zendesk_reply_error');
            toast.error(msg);
        },
    });

    const tickets = listQuery.data?.tickets ?? [];
    const agentUrl = listQuery.data?.agentUrl ?? null;
    const selected = detailQuery.data;
    const canSubmit = comment.trim().length >= 10 && subject.trim().length >= 8;

    function openCreateModal() {
        setSubjectKey('comss_duvidas');
        setMotivo('comss_duvidas');
        setSubject(`Pedido #${saleFallback} — ${t('v2.ploomes_deals.zendesk_motivo_duvidas')}`);
        setComment('');
        setSummaryOpen(false);
        setShowCreateModal(true);
    }

    function selectSubject(suggestion: SubjectSuggestion) {
        setSubjectKey(suggestion.key);
        setMotivo(suggestion.motivo || suggestion.key);
        setSubject(suggestion.subject);
    }

    if (listQuery.isPending) {
        return (
            <DetailCard title={t('v2.ploomes_deals.tabs.zendesk')} icon={<PiLifebuoyBold className="h-5 w-5" />}>
                <LoadingState variant="table" />
            </DetailCard>
        );
    }

    if (listQuery.isError) {
        return (
            <DetailCard title={t('v2.ploomes_deals.tabs.zendesk')} icon={<PiLifebuoyBold className="h-5 w-5" />}>
                <Text className="text-sm text-gray-500 dark:text-zinc-400">
                    {t('v2.ploomes_deals.zendesk_list_error')}
                </Text>
            </DetailCard>
        );
    }

    return (
        <>
            <div className={cn('grid items-start gap-6', selectedId ? 'lg:grid-cols-12' : 'grid-cols-1')}>
                <div className={cn('min-w-0', selectedId ? 'lg:col-span-5' : 'col-span-1')}>
                    <DetailCard
                        title={t('v2.ploomes_deals.zendesk_list_title')}
                        icon={<PiLifebuoyBold className="h-5 w-5" />}
                        headerExtra={(
                            <div className="flex flex-wrap items-center gap-2">
                                {agentUrl && (
                                    <a
                                        href={agentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={t('v2.ploomes_deals.zendesk_open_agent')}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                    >
                                        <PiArrowSquareOutBold className="size-3.5" />
                                        {t('v2.ploomes_deals.zendesk_open_agent')}
                                    </a>
                                )}
                                <Button size="sm" onClick={openCreateModal}>
                                    <PiLifebuoyBold className="me-1.5 size-4" />
                                    {t('v2.ploomes_deals.zendesk_ticket_button')}
                                </Button>
                            </div>
                        )}
                    >
                        {tickets.length === 0 ? (
                            <div className="space-y-3 py-6 text-center">
                                <Text className="text-sm text-gray-500 dark:text-zinc-400">
                                    {t('v2.ploomes_deals.zendesk_empty')}
                                </Text>
                                <Text className="text-xs text-gray-400 dark:text-zinc-500">
                                    {t('v2.ploomes_deals.zendesk_empty_hint')}
                                </Text>
                                {agentUrl && (
                                    <a
                                        href={agentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400"
                                    >
                                        <PiArrowSquareOutBold className="size-3.5" />
                                        {t('v2.ploomes_deals.zendesk_open_agent')}
                                    </a>
                                )}
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                                {tickets.map((ticket) => {
                                    const active = selectedId === ticket.id;
                                    return (
                                        <li key={ticket.id} className="flex items-stretch gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedId(ticket.id)}
                                                className={cn(
                                                    'flex min-w-0 flex-1 flex-col gap-1.5 px-1 py-3 text-left transition',
                                                    active
                                                        ? 'bg-rose-50/80 dark:bg-rose-950/30'
                                                        : 'hover:bg-gray-50 dark:hover:bg-zinc-900/60',
                                                )}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                                                        #{ticket.zendesk_id} — {ticket.subject}
                                                    </span>
                                                    <Badge color={statusColor(ticket.status)} className="shrink-0">
                                                        {ticket.status || '—'}
                                                    </Badge>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-zinc-400">
                                                    <DealOwnerCell
                                                        name={ticket.created_by?.name}
                                                        avatarUrl={ticket.created_by?.avatar_url}
                                                        compactName
                                                    />
                                                    <span>{formatDateTime(ticket.created_at)}</span>
                                                    <span>
                                                        {t('v2.ploomes_deals.zendesk_comments_count', {
                                                            count: ticket.comments_count,
                                                        })}
                                                    </span>
                                                </div>
                                            </button>
                                            {ticket.ticket_url && (
                                                <a
                                                    href={ticket.ticket_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={t('v2.ploomes_deals.zendesk_open_external')}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="m-2 inline-flex shrink-0 items-center justify-center self-center rounded-md border border-gray-200 p-2 text-gray-600 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-rose-700 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                                                >
                                                    <PiArrowSquareOutBold className="size-4" />
                                                </a>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </DetailCard>
                </div>

                {selectedId && (
                    <div className="min-w-0 lg:col-span-7">
                        <DetailCard
                            title={t('v2.ploomes_deals.zendesk_detail_title')}
                            icon={<PiLifebuoyBold className="h-5 w-5" />}
                            headerExtra={(
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setSelectedId(null)}
                                        className="lg:hidden"
                                    >
                                        <PiArrowLeftBold className="me-1 size-3.5" />
                                        {t('v2.ploomes_deals.zendesk_back')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => syncMutation.mutate()}
                                        isLoading={syncMutation.isPending}
                                    >
                                        <PiArrowsClockwiseBold className="me-1.5 size-3.5" />
                                        {t('v2.ploomes_deals.zendesk_sync')}
                                    </Button>
                                    {selected?.ticket_url && (
                                        <a
                                            href={selected.ticket_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                        >
                                            <PiArrowSquareOutBold className="size-3.5" />
                                            {t('v2.ploomes_deals.zendesk_open_external')}
                                        </a>
                                    )}
                                </div>
                            )}
                        >
                            {detailQuery.isPending && !selected ? (
                                <LoadingState variant="detail" />
                            ) : detailQuery.isError && !selected ? (
                                <Text className="text-sm text-gray-500 dark:text-zinc-400">
                                    {t('v2.ploomes_deals.zendesk_sync_error')}
                                </Text>
                            ) : selected ? (
                                <div className="space-y-5">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge color={statusColor(selected.status)}>
                                            {selected.status || '—'}
                                        </Badge>
                                        {selected.priority && (
                                            <Badge color="secondary">{selected.priority}</Badge>
                                        )}
                                        {selected.created_by && (
                                            <DealOwnerCell
                                                name={selected.created_by.name}
                                                avatarUrl={selected.created_by.avatar_url}
                                                compactName
                                            />
                                        )}
                                        <Text className="text-xs text-gray-500 dark:text-zinc-400">
                                            {t('v2.ploomes_deals.zendesk_last_sync', {
                                                date: formatDateTime(selected.last_synced_at),
                                            })}
                                        </Text>
                                    </div>
                                    <Text className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                                        #{selected.zendesk_id} — {selected.subject}
                                    </Text>

                                    <div className="space-y-3">
                                        <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                                            {t('v2.ploomes_deals.zendesk_history')}
                                        </Text>
                                        {(selected.comments ?? []).length === 0 ? (
                                            <Text className="text-sm text-gray-500 dark:text-zinc-400">
                                                {t('v2.ploomes_deals.zendesk_history_empty')}
                                            </Text>
                                        ) : (
                                            <ul className="space-y-3">
                                                {(selected.comments ?? []).map((item) => (
                                                    <li
                                                        key={item.id}
                                                        className={cn(
                                                            'rounded-lg border px-3 py-2.5',
                                                            item.public
                                                                ? 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900'
                                                                : 'border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/30',
                                                        )}
                                                    >
                                                        <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                                            <DealOwnerCell
                                                                name={item.author_name || item.author_email || '—'}
                                                                avatarUrl={item.author_avatar_url}
                                                                compactName
                                                            />
                                                            <Badge
                                                                color={item.public ? 'info' : 'warning'}
                                                                className="text-[10px]"
                                                            >
                                                                {item.public
                                                                    ? t('v2.ploomes_deals.zendesk_comment_public')
                                                                    : t('v2.ploomes_deals.zendesk_comment_internal')}
                                                            </Badge>
                                                            <span className="text-[11px] text-gray-400 dark:text-zinc-500">
                                                                {formatDateTime(item.zendesk_created_at)}
                                                            </span>
                                                        </div>
                                                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-zinc-300">
                                                            {item.body}
                                                        </pre>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div className="space-y-2 border-t border-gray-100 pt-4 dark:border-zinc-800">
                                        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
                                            {t('v2.ploomes_deals.zendesk_reply_label')}
                                        </label>
                                        <textarea
                                            value={replyBody}
                                            onChange={(e) => setReplyBody(e.target.value)}
                                            rows={3}
                                            placeholder={t('v2.ploomes_deals.zendesk_reply_placeholder')}
                                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                        />
                                        <div className="flex justify-end">
                                            <Button
                                                onClick={() => replyMutation.mutate()}
                                                isLoading={replyMutation.isPending}
                                                disabled={replyBody.trim().length === 0}
                                            >
                                                <PiPaperPlaneTiltBold className="me-1.5 size-4" />
                                                {t('v2.ploomes_deals.zendesk_reply_submit')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </DetailCard>
                    </div>
                )}
            </div>

            <V2Modal
                open={showCreateModal}
                onClose={() => !createMutation.isPending && setShowCreateModal(false)}
                title={t('v2.ploomes_deals.zendesk_ticket_title')}
                description={t('v2.ploomes_deals.zendesk_ticket_description')}
                size="lg"
                footer={(
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setShowCreateModal(false)}
                            disabled={createMutation.isPending}
                        >
                            {t('v2.ploomes_deals.zendesk_cancel')}
                        </Button>
                        <Button
                            color="danger"
                            onClick={() => createMutation.mutate()}
                            isLoading={createMutation.isPending}
                            disabled={!canSubmit || createMutation.isPending}
                        >
                            <PiLifebuoyBold className="me-2 size-4" />
                            {t('v2.ploomes_deals.zendesk_ticket_submit')}
                        </Button>
                    </>
                )}
            >
                <div className="space-y-5">
                    {(draftQuery.data?.routing || true) && (
                        <div className="rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">
                            {t('v2.ploomes_deals.zendesk_routing_hint', {
                                form: draftQuery.data?.routing?.form || 'Solicitações internas',
                                group: draftQuery.data?.routing?.group || 'Comissões',
                                area: draftQuery.data?.routing?.area || 'Comissões',
                            })}
                        </div>
                    )}

                    <div>
                        <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-zinc-400">
                            {t('v2.ploomes_deals.zendesk_motivo_label')}
                        </label>
                        <div className="mb-2 flex flex-wrap gap-2">
                            {suggestions.map((item) => {
                                const active = subjectKey === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => selectSubject(item)}
                                        className={cn(
                                            'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                                            active
                                                ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800',
                                        )}
                                    >
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                        <label className="mb-1 mt-3 block text-xs font-medium text-gray-600 dark:text-zinc-400">
                            {t('v2.ploomes_deals.zendesk_ticket_subject')}
                        </label>
                        <Input
                            value={subject}
                            onChange={(e) => {
                                setSubjectKey('comss_outros');
                                setMotivo('comss_outros');
                                setSubject(e.target.value);
                            }}
                            placeholder={t('v2.ploomes_deals.zendesk_ticket_subject_placeholder')}
                        />
                        <Text className="mt-1 text-[11px] text-gray-400 dark:text-zinc-500">
                            {t('v2.ploomes_deals.zendesk_subject_hint')}
                        </Text>
                    </div>

                    <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
                                {t('v2.ploomes_deals.zendesk_summary_title')}
                            </label>
                            <button
                                type="button"
                                onClick={() => setSummaryOpen((v) => !v)}
                                className="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400"
                            >
                                {summaryOpen
                                    ? t('v2.ploomes_deals.zendesk_summary_hide')
                                    : t('v2.ploomes_deals.zendesk_summary_show')}
                            </button>
                        </div>
                        <Text className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.zendesk_summary_hint')}
                        </Text>
                        {draftQuery.isPending ? (
                            <LoadingState variant="inline" />
                        ) : (
                            <pre
                                className={cn(
                                    'whitespace-pre-wrap rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 font-sans text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-300',
                                    !summaryOpen && 'max-h-28 overflow-hidden',
                                )}
                            >
                                {draftQuery.data?.summary
                                    || t('v2.ploomes_deals.zendesk_summary_fallback', { sale: saleFallback })}
                            </pre>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-zinc-400">
                            {t('v2.ploomes_deals.zendesk_ticket_comment')}
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={5}
                            placeholder={t('v2.ploomes_deals.zendesk_ticket_comment_placeholder')}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                        <Text className="mt-1 text-[11px] text-gray-400 dark:text-zinc-500">
                            {t('v2.ploomes_deals.zendesk_comment_hint')}
                        </Text>
                    </div>
                </div>
            </V2Modal>
        </>
    );
}
